'use client';

import {useI18n} from '@udemy-v2/i18n';
import {
    CardAuthors,
    CardDescription,
    CardTitle,
    FullBleedCard,
    RatingTag,
    Tag,
    TagList,
} from '@udemy-v2/react-product-card-components';

import type {CourseOption} from '@/features/instructor-profile/lib/types';
import {formatCount} from '@/features/public-profile/lib/format-stats';

import styles from './featured-courses.module.scss';

interface FeaturedCoursesProps {
    /**
     * Featured courses to display. Presentational only — this component never
     * fetches; the public profile page (and later the instructor preview tab)
     * pass already-resolved `profile.featured_courses` here.
     * `null`/`undefined`/empty renders nothing.
     */
    courses?: CourseOption[] | null;
}

const MAX_FEATURED_COURSES = 2;

export function FeaturedCourses({courses}: FeaturedCoursesProps) {
    const {gettext, ninterpolate, locale} = useI18n();

    if (!courses || courses.length === 0) {
        return null;
    }

    return (
        <section
            className={styles['featured-courses']}
            aria-labelledby="featured-courses-heading"
        >
            <h2 id="featured-courses-heading" className={styles['heading']}>
                {gettext('My top courses')}
            </h2>
            <ul className={styles['list']}>
                {courses.slice(0, MAX_FEATURED_COURSES).map((course) => {
                    const authors = course.visible_instructors
                        .map((instructor) => instructor.display_name ?? instructor.name)
                        .join(', ');

                    const tags = [];
                    if (course.rating) {
                        tags.push(<RatingTag key="rating" rating={course.rating} />);
                    }
                    if (course.num_reviews) {
                        tags.push(
                            <Tag
                                key="reviews"
                                size="small"
                                udStyle="information"
                                label={ninterpolate(
                                    '%(count)s rating',
                                    '%(count)s ratings',
                                    course.num_reviews,
                                    {count: formatCount(course.num_reviews, locale)},
                                )}
                            />,
                        );
                    }

                    return (
                        <li key={course.id} className={styles['item']}>
                            <FullBleedCard
                                className={styles['card-wrapper']}
                                primaryContentClassName={styles['card-media']}
                                backgroundImage={`url('${course.image_750x422}')`}
                                primaryContentColorScheme="transparent-dark"
                                title={
                                    <CardTitle
                                        headingLevel="h3"
                                        renderLink={CardTitle.renderAnchorTag({
                                            href: course.url,
                                        })}
                                    >
                                        {course.title}
                                    </CardTitle>
                                }
                                description={
                                    course.headline ? (
                                        <CardDescription>{course.headline}</CardDescription>
                                    ) : undefined
                                }
                                authors={
                                    authors ? (
                                        <CardAuthors
                                            label={gettext('Instructors: ')}
                                            authors={authors}
                                        />
                                    ) : undefined
                                }
                                tags={tags.length > 0 ? <TagList tags={tags} /> : undefined}
                            />
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}
